import { store } from "@App/state";
import { posthog } from "@App/utils/analytics/setup/posthog";
import i18n from "@App/utils/i18n";
import { reactDomRoot } from "@App/utils/react-dom-root";
import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { Provider } from "react-redux";
import ApolloProvider from "./Components/providers/apollo/ApolloProvider";
import "@App/utils/analytics/mappings";
import { App } from "@App/App";
import { PerimeterSearchService } from "@App/apis/graphql/perimeterSearch/v1/perimeterSearchService";
import { PerimeterSearchContext } from "@App/contexts/apis/PerimeterSearchContext";
import { EntryPageProvider } from "@App/contexts/EntryPageContext";
import { BrowserRouter } from "react-router-dom";

/**
 * The `Repareo` component serves as the root component for the application.
 * It sets up the necessary providers for state management, internationalization,
 * analytics, and API contexts, ensuring that the application is properly configured.
 *
 * Providers included:
 * - `Provider` for Redux state management.
 * - `ApolloProvider` for GraphQL integration.
 * - `I18nextProvider` for internationalization.
 * - `PostHogProvider` for analytics tracking.
 * - `PerimeterSearchContext.Provider` for perimeter search API context.
 * - `BrowserRouter` for routing within the application.
 *
 * * @returns {JSX.Element} The root component wrapped with all necessary providers.
 */
export function Repareo() {
  return (
    <Provider store={store}>
      <EntryPageProvider>
        <ApolloProvider>
          <I18nextProvider i18n={i18n}>
            <PostHogProvider client={posthog}>
              <PerimeterSearchContext.Provider value={new PerimeterSearchService()}>
                <BrowserRouter future={{ v7_relativeSplatPath: true } as any} basename={"/"}>
                  <App />
                </BrowserRouter>
              </PerimeterSearchContext.Provider>
            </PostHogProvider>
          </I18nextProvider>
        </ApolloProvider>
      </EntryPageProvider>
    </Provider>
  );
}

Sentry.init({
  dsn: "https://824319c5e9fd4e0ea7c0b17ee22c8121@o189848.ingest.sentry.io/6627087",
  beforeSend(event) {
    try {
      if (
        event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename?.match(
          /https:\/\/app.posthog.com\/.*/g,
        )
      ) {
        return null;
      }
    } catch (e) {
      console.error("Error in Sentry beforeSend:", e);
    }

    return event;
  },
  denyUrls: [/https:\/\/app.posthog.com\/.*/g],
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  replaysOnErrorSampleRate: 1.0,
  release: sentry_release,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.05,
});

if (document.getElementById("repareo-app")) {
  if (reactDomRoot === null) {
    throw new Error("ReactDOM root element not found");
  }

  const root = ReactDOM.createRoot(reactDomRoot);
  root.render(<Repareo />);
}
